"use client";

import { AlertTriangle, ExternalLink, Lock, Mail } from "lucide-react";
import Link from "next/link";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useEffect, useRef, useState, useTransition } from "react";
import { InPageApplyConfirm } from "@/components/apply/in-page-apply-confirm";
import { usePublishStickyCtaHeight } from "@/components/consent/sticky-cta-height";
import { useAiApplyPrimary } from "@/components/jobs/ai-apply-job-button";
import { ApplyEmailHandoff } from "@/components/jobs/apply-email-handoff";
import { ExtensionInstallNudge } from "@/components/jobs/extension-install-nudge";
import { useJobContext } from "@/components/jobs/job-context";
import { JobIntentSignup } from "@/components/jobs/job-intent-signup";
import { ZoneLink } from "@/components/layout/zone-link";
import { useMarketHint } from "@/components/market/market-hint-context";
import { markPostApplyPending } from "@/components/monetization/post-apply-offer";
import { PostActionSheet } from "@/components/profile/post-action-sheet";
import { buttonClass } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type {
	DestinationClass,
	DestinationKind,
} from "@/lib/analytics/destination-class";
import {
	deriveEmployerTier,
	type JobApplyOutboundProps,
} from "@/lib/analytics/events";
import type { GateImpressionProps } from "@/lib/analytics/hooks";
import { useAnalytics } from "@/lib/analytics/hooks";
import {
	type OriginSurfaceParam,
	parseContentSlugParam,
	parseOriginSurfaceParam,
	withOriginSurface,
} from "@/lib/analytics/origin-surface";
import { useExperiment } from "@/lib/analytics/use-experiment";
import {
	monthlyPriceDisplayCompact,
	perMonthWordShort,
} from "@/lib/billing/pricing-plans";
import { reloadOncePerBuild } from "@/lib/error-recovery/reload-budget";
import {
	APPLY_PAYWALL_RELAXED_FLAG,
	APPLY_PAYWALL_RELAXED_VARIANTS,
} from "@/lib/experiments/apply-paywall-relaxed";
import { useExtensionInstalled } from "@/lib/extension/use-extension-installed";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import {
	APPLY_MOMENT_SIMPLIFIED_FLAG,
	applyClicksProof,
} from "@/lib/jobs/apply-moment";
import { type ApplyRiskKind, applyRiskMessage } from "@/lib/jobs/apply-risk";
import { fitScoreBucket } from "@/lib/match/fit-bucket";
import { lowFitNotice } from "@/lib/match/low-fit";
import {
	MOBILE_APPLY_BAR_VISIBLE_QUERY,
	useClaimStickyFooter,
} from "@/lib/monetization/sticky-footer-claim";
import {
	usePartnerClickProof,
	withPartnerClickProof,
} from "@/lib/monetization/use-partner-click-proof";
import type { LandSignal } from "@/lib/personalization/land-signal";
import type { ProfileFieldId } from "@/lib/profile/field-config";
import { isServerActionSkewError } from "@/lib/profile/server-action-skew";
import { useProfileCompleteness } from "@/lib/profile/use-completeness";
import { setProfilePhone } from "@/server/actions/profile";
import { pickApplyNudge } from "@/server/actions/profile-nudge";

type Variant = "default" | "mobile-fixed-footer";

/**
 * Post-apply profile nudge. Best-effort: it runs after the seeker already
 * clicked out to the employer, so any failure is swallowed. Unguarded, a
 * stale Server Action id after a deploy (`UnrecognizedActionError`, PostHog
 * $exception on /jobs/* pages 2026-10-21) rejected inside the transition and
 * took the job page down to its error boundary at the apply moment.
 */
async function loadPostApplyNudge(
	setField: (field: ProfileFieldId) => void,
): Promise<void> {
	try {
		const r = await pickApplyNudge();
		if (r.ok && r.nudge) setField(r.nudge.field);
	} catch {
		// Non-essential — never let it break the page.
	}
}

type Props = {
	applyUrl: string;
	isInternalApply: boolean;
	/**
	 * Apply-by-email job: `applyUrl` is a Gmail compose URL. Swaps the button
	 * to an "Email to apply" label + mail icon; still opens in a new tab.
	 */
	isEmailApply?: boolean;
	/**
	 * The apply email was DERIVED (best-effort recruiter lead-gen: company →
	 * careers@domain), not extracted from the posting. Surfaces an honest note so
	 * the user verifies the address in the Gmail draft before sending.
	 */
	isGuessedEmail?: boolean;
	locale: string;
	jobId: string;
	jobPathname: string;
	summaryGenerationModel?: string | null;
	t: Dictionary;
	/** Source platform name (e.g. `linkedin`, `wellfound`) from the job row. */
	sourcePlatform: string;
	payPresent: boolean;
	payCurrency?: string | null;
	postedAgeHours?: number;
	isAiEnriched: boolean;
	categoryId?: string | null;
	categoryType?: "normal" | "rich" | null;
	/**
	 * Per-job hireability indicator. Threaded into the apply event so the
	 * NSM (Weekly Qualified Applies) can filter by `land_signal_tier >= 2`.
	 */
	landSignal?: LandSignal | null;
	/** Linked `employer_orders.id` for self-serve jobs; NULL for organic. */
	employerOrderId?: string | null;
	/** Raw `employer_orders.product_key` — passed through `deriveEmployerTier`. */
	employerOrderProductKey?: string | null;
	/**
	 * `"default"` renders the inline button (used in the desktop sidebar).
	 * `"mobile-fixed-footer"` wraps the same button in a fixed-bottom bar
	 * hidden above `lg`, so mobile users see a persistent apply CTA that
	 * doesn't get pushed below the job description.
	 */
	variant?: Variant;
	/** Number of related jobs available on this page. When > 0 and the user
	 * is gated, a "View N similar jobs" anchor link is shown below the gate
	 * so non-converters have a next step instead of a dead end. */
	similarJobCount?: number;
	/**
	 * The single blocking risk this job carries (`@/lib/jobs/apply-risk`), or
	 * null when it carries none. Rendered ONLY by the `mobile-fixed-footer`
	 * variant, as a one-line mirror of the strip under the `<h1>`.
	 *
	 * The full notices live on the page. This exists because the page and the
	 * button are not in the same place on a phone: the footer is `fixed
	 * bottom-0` and tappable from first paint, so a seeker who scrolls past the
	 * strip and then taps Apply has never seen the warning at the moment it
	 * mattered. The warning has to travel with the button.
	 *
	 * Not rendered on the `default` (desktop sidebar) variant: there the strip
	 * and the CTA share a viewport, so mirroring would just say it twice.
	 */
	blockingRisk?: ApplyRiskKind | null;
	/**
	 * Real `jobs.apply_clicks`. Only read behind `apply-moment-simplified`, and
	 * only rendered at >= 5 (`applyClicksProof`) — never estimated.
	 */
	applyClicks?: number | null;
	/**
	 * The supported-ATS bucket (e.g. "greenhouse") when this job's apply form is
	 * one the extension can autofill, else null. Drives the contextual
	 * install-extension nudge for signed-in users who don't have it installed.
	 */
	atsName?: string | null;
	/**
	 * `classifyDestination()` (src/lib/analytics/destination-class.ts) run
	 * server-side against the job's real external apply URL (`resolvedApplyUrl
	 * ?? sourceUrl`) — the same input `atsName` above is derived from. NOT
	 * derived from `applyUrl`: for internal/email applies `applyUrl` is an
	 * internal `/out/...` redirect or a Gmail compose link, not the real
	 * destination host. Threaded onto the outbound-click event only; null
	 * when not resolved server-side.
	 */
	destinationClass?: DestinationClass | null;
	/**
	 * `describeApplyDestination()` run server-side on the same URL. "ats" /
	 * "linkedin" with a label → button reads "Apply via {label}";
	 * "aggregator" / "partner" with a label → plain button plus an honest
	 * "Via {label} — partner site" line. No label → current copy unchanged.
	 * Also carried on the outbound-click event.
	 */
	destinationKind?: DestinationKind | null;
	destinationLabel?: string | null;
	/**
	 * `job.applyMode === "passthrough"` — a CPC partner-feed job (Jobg8,
	 * Careerjet) whose outbound click is billable. Only these mint a
	 * browser-proof token: `/out/[jobId]` refuses to forward a partner click
	 * without one, and minting on non-billable jobs would buy a request per job
	 * view for nothing. See src/lib/monetization/partner-click-proof.ts.
	 */
	isPartnerPassthrough?: boolean;
	/** Company name, so the in-page confirm can name who the visitor applied to. */
	company?: string | null;
	/**
	 * Job title, and the raw apply-by-email address (`job.applyEmail` — same
	 * value `buildJobApplyGmailUrl` used to build `applyUrl` above). Passed
	 * through to `ApplyEmailHandoff` ONLY so its `guest-email-apply-draft`
	 * `template` variant can build a ready-to-send email for a guest, who has
	 * no session for the server action to build one from. Unused by every other
	 * branch. `applyEmail` is null whenever `isEmailApply` is false.
	 */
	jobTitle?: string;
	applyEmail?: string | null;
	/**
	 * `apply-inpage-confirm-v1`, resolved SERVER-side by the page and passed
	 * down. Deliberately not a client `useExperiment` read: that starts on its
	 * control default and only leaves it inside PostHog's `onFeatureFlags`
	 * callback, so a visitor whose flags never arrive (consent, an ad-blocker, a
	 * slow `/decide`) silently gets the off branch — the exact defect #5113 fixed.
	 */
	inpageConfirmEnabled?: boolean;
};

export function JobApplyCta({
	applyUrl,
	isInternalApply,
	isEmailApply = false,
	isGuessedEmail = false,
	locale,
	jobId,
	jobPathname,
	summaryGenerationModel,
	t,
	variant = "default",
	sourcePlatform,
	payPresent,
	payCurrency,
	postedAgeHours,
	isAiEnriched,
	categoryId,
	categoryType,
	landSignal,
	employerOrderId,
	employerOrderProductKey,
	similarJobCount = 0,
	atsName = null,
	destinationClass = null,
	destinationKind = null,
	destinationLabel = null,
	blockingRisk = null,
	applyClicks = null,
	isPartnerPassthrough: _isPartnerPassthrough = false,
	company = null,
	jobTitle,
	applyEmail = null,
	inpageConfirmEnabled = false,
}: Props) {
	// Per-user gating resolves client-side via the job-context overlay so the
	// page itself can render statically (guest view) and be edge-cached.
	// `canApply` mirrors "is authenticated"; `isPreview` is the salary/apply
	// paywall state; `userState` drives the upsell copy.
	const { userState, isPreview, canApply, loading, fitScoreBreakdown } =
		useJobContext();
	// Sole switch for the apply-moment declutter. `undefined` (flags not loaded
	// or PostHog blocked) is the safe-off state: today's layout.
	const simplified =
		useFeatureFlagEnabled(APPLY_MOMENT_SIMPLIFIED_FLAG) === true;
	const {
		posthog,
		trackApplyClick,
		trackGateImpression,
		trackGateCtaClick,
		trackGateAbandoned,
	} = useAnalytics();
	// Client-side hint (ISR-safe, see market-hint-context.tsx) — this page is
	// revalidate=3600, so market can't be resolved server-side without forcing
	// it dynamic. Threaded into pricing_cta_clicked so these three job-detail
	// upsell surfaces aren't invisible in the market breakdown (go-global spec
	// PR-2 follow-up, docs/plans/backlog/go-global-execution-2026-07-31.md).
	const market = useMarketHint();
	// Daily-apply paywall relaxation experiment — "apply-paywall-relaxed" (T5b).
	// Ships fully OFF; flagged self-measurement of whether the hard upgrade
	// wall costs more retention than it earns. `control` keeps today's Lock
	// wall; `variant` renders the real apply button instead (same markup as
	// the canApply===true branch below — the flag removes the hard block, not
	// the soft Pro upsell). This SAME flag key is mirrored server-side in
	// out/[jobId]/[[...slug]]/route.ts via getServerFeatureFlagForUser — see
	// src/lib/experiments/apply-paywall-relaxed.ts.
	const { content: paywallRelaxed } = useExperiment<boolean>({
		flagKey: APPLY_PAYWALL_RELAXED_FLAG,
		variants: APPLY_PAYWALL_RELAXED_VARIANTS,
	});
	const [, startTransition] = useTransition();
	const [postApplyField, setPostApplyField] = useState<ProfileFieldId | null>(
		null,
	);

	// FIX-11(b) — detect a landing from the new-jobs-alert email so the
	// outbound click attributes to `origin_surface: "job_alert_email"`
	// instead of the generic "detail". Every outbound email link already
	// carries `utm_medium=<notification.type>` (applyEmailUtm in
	// src/lib/email/utm.ts) — no new email-side query param needed. Read
	// client-side only (`window.location.search`, not `searchParams`)
	// because the job-detail page is ISR-cached as the guest view — the
	// query string must never influence the cached HTML (see
	// src/app/CLAUDE.md).
	//
	// Same mechanism for a landing from a blog post's job list, which tags
	// its links `?origin_surface=blog&content_slug=<slug>`
	// (src/lib/analytics/origin-surface.ts). Attribution only — the `/out`
	// route still treats only the email surface as billing provenance.
	const [landingOrigin, setLandingOrigin] = useState<{
		surface: OriginSurfaceParam;
		contentSlug?: string;
	} | null>(null);
	useEffect(() => {
		try {
			const params = new URLSearchParams(window.location.search);
			if (
				params.get("utm_source") === "email" &&
				params.get("utm_medium") === "new_jobs_alert"
			) {
				setLandingOrigin({ surface: "job_alert_email" });
				return;
			}
			const surface = parseOriginSurfaceParam(params.get("origin_surface"));
			if (surface) {
				setLandingOrigin({
					surface,
					contentSlug: parseContentSlugParam(
						surface,
						params.get("content_slug"),
					),
				});
			}
		} catch {
			// URLSearchParams unavailable — skip silently, never block render.
		}
	}, []);
	// Tags the outbound /out (or internal apply-form) URL so the redirect
	// route's own `apply_hop_resolution` event attributes back too — the
	// static, ISR-baked `applyUrl` prop carries no per-visitor query string.
	// Excluded for `isEmailApply` (a Gmail compose URL, not a lokerdollar.com
	// path — appending a query param there would corrupt the mailto/compose link).
	const originTaggedApplyUrl =
		landingOrigin && !isEmailApply
			? withOriginSurface(
					applyUrl,
					landingOrigin.surface,
					landingOrigin.contentSlug,
				)
			: applyUrl;

	// Browser-proof token for CPC partner clicks (2026-08-24). Minted after
	// hydration, so its presence on the outbound URL is itself the evidence that
	// a browser ran this page — the one thing request headers cannot establish
	// and the reason 40 "eligible" US partner clicks coexisted with 6 US
	// job-page views over 2026-08-21→23. Null for every non-passthrough job (and
	// whenever minting fails), in which case the URL is byte-identical to what
	// it was before this shipped. Never appended to an `isEmailApply` Gmail
	// compose URL, same carve-out as `origin_surface` above.
	// Minted for EVERY apply-through-/out job, not only the billable
	// passthrough ones (2026-09-06). The token is now the guest-apply cap's
	// only way to tell a person from a header-perfect crawler, so a job that
	// mints nothing is a job whose cap the swarm eats. `isEmailApply` still
	// opts out: a mailto CTA never travels through `/out`, so it has no hop to
	// prove. Cost measured before widening: ~319 job views/day, so ~9.6k extra
	// same-origin mints/month — an HMAC each, no D1 and no KV.
	const partnerClickProof = usePartnerClickProof(jobId, !isEmailApply);
	const outboundApplyUrl = withPartnerClickProof(
		originTaggedApplyUrl,
		isEmailApply ? null : partnerClickProof,
	);

	// Every branch below renders the fixed-footer bar via `wrap()` when this
	// variant is used (only the content inside varies by gate state) — claim
	// the mobile bottom slot so the `mobile_sticky` ad bar yields instead of
	// visually covering the primary Apply button.
	// The bar this claims for is `lg:hidden` (see `wrap()` below), so pass the
	// viewport it is actually visible at. Without it the claim is held on
	// desktop too, where the bar is CSS-hidden and occupies nothing — which
	// silently suppressed every apply-moment surface, the paid one included.
	// Stackable (2026-09-28): the bottom ad bar sits 8px ABOVE this bar instead
	// of vanishing on every mobile job page. The Apply button keeps the bottom
	// edge and is never covered; every other ambient bar still yields to it.
	useClaimStickyFooter(
		variant === "mobile-fixed-footer",
		MOBILE_APPLY_BAR_VISIBLE_QUERY,
		{ allowAdAbove: true },
	);
	// Publishes this bar's height so the consent banner stacks above it —
	// the bar itself never moves (see sticky-cta-height.ts for the CLS cause).
	const fixedFooterRef = useRef<HTMLDivElement>(null);
	usePublishStickyCtaHeight(fixedFooterRef, variant === "mobile-fixed-footer");

	// The gate this instance actually put on screen, and when. Set only by a
	// captured impression; read by the exit listener below.
	const armedGateRef = useRef<{
		props: GateImpressionProps;
		shownAt: number;
	} | null>(null);

	// gate_abandoned — the visitor met the wall and left without taking its CTA.
	//
	// Until now this was only reachable by subtracting `gate_cta_clicked` from
	// `gate_impression`, which cannot tell "read the wall and refused" from
	// "scrolled past without seeing it". Those have opposite product meanings
	// and the subtraction merges them; `dwell_ms` is what separates them.
	//
	// Both `pagehide` and `visibilitychange` are registered because neither is
	// sufficient alone: iOS Safari frequently kills a tab without `pagehide`,
	// and a desktop tab close does not always go through `hidden` first. The
	// duplicate that this pair produces on a real mobile app-switch is absorbed
	// by the once-per-gate guard in `trackGateAbandoned`.
	//
	// `beforeunload` is deliberately NOT used: it is unreliable on mobile and
	// registering it opts the page out of the bfcache, which would be a real
	// user-facing cost for a telemetry event.
	useEffect(() => {
		const fire = (exit: "pagehide" | "hidden") => {
			const armed = armedGateRef.current;
			if (!armed) return;
			trackGateAbandoned({
				...armed.props,
				dwell_ms: Date.now() - armed.shownAt,
				exit,
			});
		};
		const onPageHide = () => fire("pagehide");
		const onVisibility = () => {
			if (document.visibilityState === "hidden") fire("hidden");
		};
		window.addEventListener("pagehide", onPageHide);
		document.addEventListener("visibilitychange", onVisibility);
		return () => {
			window.removeEventListener("pagehide", onPageHide);
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [trackGateAbandoned]);

	// Fire gate_impression (client-side) so PostHog uses the real browser
	// distinct ID and the surface_id pairs correctly with gate_cta_clicked.
	// Previously fired as `job_apply_auth_gate_viewed` (different event name),
	// which broke the gate→CTA funnel in PostHog.
	//
	// Guard: only fire for hard gates, not the soft-upsell (free + canApply=true)
	// where the Apply button is still shown. The former `if (!isPreview) return`
	// guard caused gate_impression to fire even for the soft-upsell, inflating
	// impressions vs. CTA clicks and breaking the funnel ratio.
	useEffect(() => {
		// `useJobContext()` seeds `userState: "guest"` for EVERY visitor (real
		// guest and already-authenticated alike) until `/api/me/job-context`
		// resolves (job-detail-client.tsx:55-58) — firing here before that
		// resolves miscounts an authenticated mount as a guest gate impression.
		// Consumers at job-detail-client.tsx:680/773 already guard on `loading`;
		// this effect didn't (T3, docs/plans/session-replay-ux-audit-2026-08-02.md).
		if (loading) return;
		// The guest signup wall was retired on 2026-09-07 (see the note above the
		// guest apply CTA below), so `apply_signup_gate` can no longer fire and
		// the upgrade wall is the only gate left on this surface.
		// It fires whenever canApply is false (daily-cap or category lock),
		// independent of isPreview so it captures the daily-cap redirect case too.
		const isUpgradeGate =
			(userState === "free" || userState === "open_profile") && !canApply;
		if (!isUpgradeGate) return;
		// This component mounts twice per job-detail render — the sidebar
		// ("default") and the mobile fixed-footer variant both render
		// unconditionally, CSS (not React) decides which is on screen — so
		// without this guard both instances fire the same impression,
		// double-counting it (W4-6, docs/plans/archive/telemetry-vs-reality-gap-2026-07-23.md).
		// Mirrors the `variant === "default"` scoping already used for the
		// profile-incomplete banner below.
		if (variant !== "default") return;
		const gateProps: GateImpressionProps = {
			surface_id: "apply_upgrade_gate",
			gate_type: "upgrade",
			trigger: "view_apply",
			job_id: jobId,
			// Which wall this actually is. `canApply === false` covers BOTH the
			// daily-apply cap and a rich-category lock, and without this the two
			// are indistinguishable downstream — so a daily-cap funnel could never
			// be isolated from a category-lock funnel on this surface.
			gate_cause: categoryType === "rich" ? "rich_category" : "daily_cap",
			// The `apply-paywall-relaxed` arm this user is in.
			//
			// Both arms hit the daily-apply cap, but they are walled on DIFFERENT
			// surfaces: the variant arm is let through here and only meets the
			// server wall at /out (`daily_apply_wall`), while the control arm is
			// stopped right here and never reaches /out at all. So the two arms
			// appear on disjoint surfaces and no funnel could compare them — over
			// 14d `daily_apply_wall` showed 743 events / 40 users on the variant
			// side against 12 / 3 on control, which reads like broken bucketing
			// but is purely an artifact of where each arm is stopped. (Bucketing
			// is healthy: $feature_flag_called shows 135 control / 116 variant
			// users.) Tagging the arm HERE puts both arms on one comparable
			// surface. See src/lib/experiments/apply-paywall-relaxed.ts.
			paywall_relaxed: paywallRelaxed,
			// Explicit even though this effect now never fires while `loading` —
			// makes "was auth actually resolved when this wall showed" a queryable
			// property instead of an implicit guarantee that silently breaks again.
			resolved: true,
		};
		// Arm abandonment ONLY when the impression was actually captured. The
		// per-session cap (gate-impression-cap.ts) silently drops impressions past
		// its limit, and pairing an abandon with a dropped impression would put
		// the funnel above 100%.
		if (trackGateImpression(gateProps)) {
			armedGateRef.current = { props: gateProps, shownAt: Date.now() };
		}
		// Keep the legacy event alive for dashboards that still reference it.
		posthog.capture("job_apply_auth_gate_viewed", {
			job_id: jobId,
			user_state: userState,
			upgrade_target: "pro_upgrade",
			surface: variant,
			resolved: true,
		});
	}, [
		loading,
		canApply,
		jobId,
		userState,
		variant,
		posthog,
		trackGateImpression,
		categoryType,
		paywallRelaxed,
	]);

	const surface =
		variant === "mobile-fixed-footer"
			? "job_detail_mobile_footer"
			: "job_detail";

	// Name the destination at the click moment (2026-10-10: 70% of post-apply
	// confirm answers were "not_applied"; 38% of outbound hops landed on an
	// aggregator). A direct form names its ATS on the button itself; an
	// aggregator/partner hop keeps the plain button and adds one honest line.
	// No label → copy unchanged. Never a raw URL — labels come from a fixed map.
	// Email/internal applies get no destination props from the page.
	const destinationButtonLabel =
		!isEmailApply &&
		destinationLabel &&
		(destinationKind === "ats" || destinationKind === "linkedin")
			? (t.applyNowVia || "Apply via {destination}").replace(
					"{destination}",
					destinationLabel,
				)
			: null;
	// A discussion-thread destination (Hacker News "Who is hiring" comment)
	// says so on the button, and the mobile footer adds one line on where the
	// apply details are (desktop shows <DestinationNotice> beside the sidebar
	// CTA). 2026-10-30: a seeker clicked a plain "Lamar sekarang" and was
	// surprised to land on a bare HN comment.
	const threadButtonLabel =
		!isEmailApply && destinationLabel && destinationKind === "discussion"
			? (t.applyViaThread || "Apply via the {destination} post").replace(
					"{destination}",
					destinationLabel,
				)
			: null;
	const threadNote =
		!isEmailApply &&
		destinationKind === "discussion" &&
		variant === "mobile-fixed-footer" ? (
			<p
				data-testid="apply-thread-notice"
				className="mt-1.5 text-center text-xs leading-4 text-muted-foreground"
			>
				{t.applyThreadNote ||
					(locale === "id"
						? "Email/link lamaran ada di komentar pemberi kerja."
						: "The apply email or link is in the employer's comment.")}
			</p>
		) : null;
	const destinationNoteText =
		!isEmailApply &&
		destinationLabel &&
		(destinationKind === "aggregator" || destinationKind === "partner")
			? (
					t.applyViaPartnerNotice ||
					"Via {destination} — you'll be taken to a partner site"
				).replace("{destination}", destinationLabel)
			: null;
	// Server-derived props → identical in SSR and hydration, so this one-line
	// (truncated) slot never shifts layout after paint.
	const destinationNote = destinationNoteText ? (
		<p
			data-testid="apply-destination-note"
			data-destination-kind={destinationKind ?? undefined}
			className="mt-1.5 truncate text-center text-xs leading-4 text-muted-foreground"
		>
			{destinationNoteText}
		</p>
	) : (
		threadNote
	);
	// AI Apply is the primary action on this job (Greenhouse/Lever/Ashby,
	// `ai-apply-everywhere` on, not low-fit, post-hydration — see
	// `useAiApplyPrimary`): the normal button steps down to a secondary
	// "Apply manually". Only the desktop sidebar instance gets `atsName`, so
	// the mobile footer is unaffected. Email/internal applies have no ATS.
	const aiApplyPrimary = useAiApplyPrimary(atsName);
	const applyButtonVariant = aiApplyPrimary ? "secondary" : "primary";
	// Apply-by-email jobs swap the unlocked CTA to a mail-styled "Email to apply".
	const applyLabel = aiApplyPrimary
		? locale === "id"
			? "Lamar manual"
			: "Apply manually"
		: isEmailApply
			? locale === "id"
				? "Email lamaran"
				: "Email to apply"
			: (destinationButtonLabel ??
				threadButtonLabel ??
				(t.applyNow || "Apply Now"));
	// e2e handle: the button names the discussion thread (not plain "Apply now").
	const applyLabelTestId =
		!aiApplyPrimary && !isEmailApply && threadButtonLabel
			? "apply-cta-thread-label"
			: undefined;
	const ApplyIcon = isEmailApply ? Mail : ExternalLink;
	// Honest note for a derived (best-effort) careers inbox — the user reviews the
	// Gmail draft before sending, so a wrong guess is caught, not blasted.
	const guessedEmailNote = isGuessedEmail
		? locale === "id"
			? "Kontak karier perkiraan — cek alamatnya sebelum kirim."
			: "Best-effort careers inbox — check the address before sending."
		: null;
	// Apply-by-email handoff: on ANY email-apply posting — derived addresses
	// included — we prepare the full application (tailored cover letter + CV)
	// and open it in the seeker's OWN mail client. The previous CTA here sent
	// from `hello@lokerdollar.com` and was therefore restricted to the ~242
	// extracted-address jobs; nothing leaves our domain now, so the derived tier
	// is surfaced with an honest label instead of hidden. On these postings the
	// handoff IS the apply action, so it takes the primary button and the raw
	// Gmail-compose link demotes to a secondary text link. Guests get it too: it
	// renders its own "log in" blocker (the raw Gmail-compose link is dead in
	// X/Twitter in-app browsers, 31 sessions in 48h), and a returning seeker's
	// draft is prepared for them after login.
	const showEmailHandoff = isEmailApply;

	// The page mounts a desktop sidebar CTA and a mobile footer CTA at once. Only
	// the one actually on screen may auto-prepare after login, or the draft ends
	// up in the hidden instance. Unknown until mount (SSR/jsdom) means neither.
	// `MOBILE_APPLY_BAR_VISIBLE_QUERY` is the footer's own `lg:hidden` contract.
	const [mobileBarVisible, setMobileBarVisible] = useState<boolean | null>(
		null,
	);
	useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return;
		const mq = window.matchMedia(MOBILE_APPLY_BAR_VISIBLE_QUERY);
		setMobileBarVisible(mq.matches);
		const onChange = (e: MediaQueryListEvent) => setMobileBarVisible(e.matches);
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, []);
	const autoPrepareOnReturn =
		mobileBarVisible === null
			? false
			: variant === "mobile-fixed-footer"
				? mobileBarVisible
				: !mobileBarVisible;

	const originSurface: JobApplyOutboundProps["origin_surface"] = landingOrigin
		? landingOrigin.surface
		: variant === "mobile-fixed-footer"
			? "detail_mobile_footer"
			: "detail";

	// Profile-completeness banner — surfaced only for signed-in users on
	// the desktop sidebar layout. Mobile footer is too narrow for a
	// multi-line warning. Render is purely additive — never blocks the
	// Apply click.
	//
	// Scoped to users who actually have the extension installed: the
	// required-field set here (name/email/phone/resumeText) is literally
	// the union of what the bundled ATS adapters need for autofill (see
	// `src/lib/profile/completeness.ts`), so the "autofill may miss
	// fields" warning is only relevant — and only true — for a visitor who
	// can actually use autofill. Previously this rendered for every
	// signed-in desktop user regardless of extension presence.
	const isSignedIn = userState !== "guest";
	const completeness = useProfileCompleteness(isSignedIn);
	const extensionInstalled = useExtensionInstalled();
	const showIncompleteBanner =
		isSignedIn &&
		!completeness.loading &&
		completeness.missingRequired.length > 0 &&
		variant === "default" &&
		extensionInstalled === true;
	const bannerFired = useRef(false);
	useEffect(() => {
		if (!showIncompleteBanner || bannerFired.current) return;
		bannerFired.current = true;
		posthog.capture("apply_warning_profile_incomplete_shown", {
			job_id: jobId,
			user_state: userState,
			missing_keys: completeness.missingRequired,
			has_resume: completeness.hasResume,
			surface,
		});
	}, [
		showIncompleteBanner,
		jobId,
		userState,
		completeness.missingRequired,
		completeness.hasResume,
		posthog,
		surface,
	]);
	const banner = showIncompleteBanner ? (
		<ProfileIncompleteBanner
			locale={locale}
			missingKeys={completeness.missingRequired}
			t={t}
		/>
	) : null;

	const buildApplyPayload = (
		lockState: JobApplyOutboundProps["lock_state"],
	): JobApplyOutboundProps => ({
		origin_surface: originSurface,
		...(landingOrigin?.contentSlug
			? { content_slug: landingOrigin.contentSlug }
			: {}),
		source_platform: sourcePlatform,
		pay_present: payPresent,
		pay_currency: payCurrency ?? null,
		posted_age_hours: postedAgeHours,
		is_ai_enriched: isAiEnriched,
		category_id: categoryId ?? null,
		category_type: categoryType ?? null,
		lock_state: lockState,
		user_state: userState,
		page_type: "job_detail",
		locale: locale ?? "unknown",
		land_signal_tier: landSignal?.tier ?? null,
		land_signal_score: landSignal?.score ?? null,
		employer_id: employerOrderId ?? null,
		employer_tier: deriveEmployerTier(employerOrderProductKey),
		fit_score_bucket: fitScoreBreakdown
			? fitScoreBucket(fitScoreBreakdown.score)
			: null,
		destination_class: destinationClass,
		destination_kind: destinationKind,
		destination_label: destinationLabel,
	});

	// One-line mirror of the blocking-risk strip, rendered INSIDE the fixed bar
	// above the button. `data-risk-kind` names which branch rendered so an e2e
	// can assert the invariant ("the bar states the risk it was given") without
	// pinning copy — the `data-blurb-source` precedent, see tests/e2e/CLAUDE.md.
	//
	// One amber treatment for all four kinds on purpose: severity is carried by
	// `data-risk-kind` and by which risk won precedence, not by a second hue.
	// DESIGN.md's One Voice Rule leaves exactly one status color for "warning",
	// and four colors on a 65px bar would read as decoration.
	//
	// The message itself is `text-foreground`, not `text-warning`: the warning
	// token as 12px text on its own 10% tint measures ~4.45:1 in light mode,
	// just under AA — the same near-miss class as the two contrast failures the
	// audit found on this page. The hue is carried by the border, the tint and
	// the icon (UI components, 3:1) where it is comfortably above the bar.
	const riskMirror =
		variant === "mobile-fixed-footer" && blockingRisk && !simplified ? (
			<p
				role="note"
				data-testid="apply-risk-mirror"
				data-risk-kind={blockingRisk}
				className="mb-2 flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning/10 px-2 py-1.5 text-xs leading-snug text-foreground"
			>
				<AlertTriangle
					className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning"
					aria-hidden="true"
				/>
				<span>{applyRiskMessage(blockingRisk, locale)}</span>
			</p>
		) : null;

	// The in-page "did you apply?" ask. Rendered inside `wrap` so BOTH placements
	// get it from one place, and so the mobile copy sits inside the footer that
	// `useClaimStickyFooter` above already claims — the bottom slot is arbitrated
	// there, so this cannot be covered by an ambient nag or an offer the way a
	// separately-positioned card could be (sticky-footer-claim.tsx documents that
	// class). It renders nothing until an apply click for THIS job fires
	// `ld:apply-clicked`, so it costs an idle listener and no layout otherwise.
	const inPageConfirm = (
		<InPageApplyConfirm
			jobId={jobId}
			company={company}
			locale={locale}
			enabled={inpageConfirmEnabled}
		/>
	);

	// e2e handle: "pending" in the cached HTML and the first client render
	// (both seed `loading: true`, so no hydration mismatch), "resolved" once the
	// session check settles. Until then the CTA still works but every
	// post-apply surface gated on a resolved guest (`JobIntentSignup` below)
	// ignores the click, so a spec asserting one must wait for "resolved".
	const authState = loading ? "pending" : "resolved";

	// `job-intent-signup`. Post-apply ask for resolved guests on an external,
	// non-email apply. It renders after the apply, never in front of it, and an
	// unresolved flag renders nothing (see the module doc for #5113).
	const intentSignup = (
		<JobIntentSignup
			jobId={jobId}
			jobPathname={jobPathname}
			locale={locale}
			placement={variant === "mobile-fixed-footer" ? "footer" : "sidebar"}
			eligible={
				!loading && userState === "guest" && !isInternalApply && !isEmailApply
			}
			onScreen={autoPrepareOnReturn}
		/>
	);

	const wrap = (children: React.ReactNode) =>
		variant === "mobile-fixed-footer" ? (
			// No backdrop-blur / transform / filter here — those create a new
			// containing block which would scope the PostActionSheet's `fixed`
			// positioning to this footer instead of the viewport.
			// z-40 is correct: stays above every ambient nag (now z-30 — feedback
			// widget, discovery nudge). The bottom slot is arbitrated by
			// useClaimStickyFooter above, not by z-index alone — see
			// src/lib/monetization/sticky-footer-claim.tsx. The consent banner
			// (cookie-banner.tsx) shares this z-40; this bar stays pinned at
			// bottom-0 and publishes its height (--sticky-cta-h), and the banner
			// stacks ABOVE it — layout offset, not stacking order, keeps the apply
			// CTA reachable while consent is undecided. Never make this bar's
			// position depend on the banner: it is already painted when the banner
			// mounts, so every offset change is a layout shift (mobile job-detail
			// CLS 0.10–0.14, 2026-09-27).
			<>
				{/* In-flow room for the ad bar stacked above this one (it publishes
				    --ad-bar-h while shown, 0px otherwise). The page's own bottom
				    padding already clears this bar and the 8px gap; this adds the
				    ad's height so the last content scrolls clear of both. */}
				<div aria-hidden="true" className="h-[var(--ad-bar-h,0px)] lg:hidden" />
				<div
					ref={fixedFooterRef}
					data-testid="job-apply-fixed-footer"
					className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background px-4 pb-[env(safe-area-inset-bottom)] pt-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] lg:hidden"
				>
					{riskMirror}
					{children}
					{intentSignup}
					{inPageConfirm}
				</div>
			</>
		) : (
			<>
				{children}
				{intentSignup}
				{inPageConfirm}
			</>
		);

	if (!isPreview) {
		// External applies open in a new tab — the original page stays mounted, so
		// we can fire the nudge picker right after click and render a sheet.
		// Internal applies navigate away in this tab; skip the sheet there.
		const isExternal = !isInternalApply;
		const isLoggedIn = userState !== "guest";

		const onClick = () => {
			// `trackApplyClick` runs three side effects synchronously — a PostHog
			// capture, a `/api/telemetry/track` POST, and a `localStorage.setItem` —
			// all inside the click, before the browser can respond to the tap. This
			// element is the job page's worst interaction: INP 276 ms measured on a
			// throttled mobile profile (2026-09-03), of which 174-198 ms is
			// processing, against a real-user mobile INP p75 of 268 ms. Both put it
			// over Google's 200 ms bar.
			//
			// Deferred by one macrotask ONLY for an external apply, where
			// `target="_blank"` keeps this page mounted so the work is guaranteed to
			// run. An internal apply navigates in THIS tab, where a deferred
			// callback can be dropped mid-unload — there the events matter more than
			// the milliseconds, so it stays synchronous.
			const fireApplyTelemetry = () =>
				trackApplyClick(jobId, buildApplyPayload("unlocked"), {
					surface,
					summary_generation_model: summaryGenerationModel ?? null,
				});
			if (isExternal) {
				setTimeout(fireApplyTelemetry, 0);
				markPostApplyPending(jobId, { company, title: jobTitle });
			} else {
				fireApplyTelemetry();
			}
			if (isExternal && isLoggedIn) {
				startTransition(() => loadPostApplyNudge(setPostApplyField));
			}
		};

		return wrap(
			<>
				{banner}
				{showEmailHandoff ? (
					<>
						<ApplyEmailHandoff
							jobId={jobId}
							locale={locale}
							variant="primary"
							autoPrepareOnReturn={autoPrepareOnReturn}
							jobTitle={jobTitle}
							company={company}
							applyEmail={applyEmail}
						/>
						<p className="text-center text-xs text-muted-foreground">
							<Link
								href={outboundApplyUrl}
								target="_blank"
								rel="nofollow"
								onClick={onClick}
								className="rounded font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
							>
								{locale === "id"
									? "Atau tulis sendiri di Gmail"
									: "Or write it yourself in Gmail"}
							</Link>
						</p>
					</>
				) : (
					<>
						<Link
							href={outboundApplyUrl}
							target={isInternalApply ? undefined : "_blank"}
							rel={isInternalApply ? undefined : "nofollow"}
							onClick={onClick}
							data-testid="job-apply-cta"
							data-auth-state={authState}
							data-destination-kind={destinationKind ?? undefined}
							className={buttonClass({
								variant: applyButtonVariant,
								size: "lg",
								shape: "xl",
								fullWidth: true,
								className: "group",
							})}
						>
							<span data-testid={applyLabelTestId} className="min-w-0 truncate">
								{applyLabel}
							</span>
							<ExternalLink className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
						</Link>
						{destinationNote}
					</>
				)}

				{/* P1-B: surface cover letter at the highest-intent moment */}
				{isLoggedIn && isExternal && !showEmailHandoff && (
					<p className="text-center text-xs text-muted-foreground">
						<ZoneLink
							href={`/${locale}/resume`}
							className="font-medium text-primary underline-offset-4 hover:underline"
						>
							{t.generateCoverLetter}
						</ZoneLink>{" "}
						{t.withAI}
					</p>
				)}

				{/* Contextual install nudge — supported-ATS job, no extension. Guests
				    too: requiring sign-in hid it from most visitors (194 shows in 30d). */}
				{isExternal && atsName ? (
					<ExtensionInstallNudge
						locale={locale}
						jobId={jobId}
						atsName={atsName}
					/>
				) : null}

				{postApplyField ? (
					<PostActionSheet
						field={postApplyField}
						surface="post_apply"
						eyebrow={
							t.applicationSentEyebrow ??
							"Application sent · boost your profile"
						}
						autoDismissMs={10_000}
						t={t}
						onClose={() => setPostApplyField(null)}
					/>
				) : null}
			</>,
		);
	}

	// Shared markup for the unlocked apply button + soft Pro upsell — used both
	// by the canApply===true branch (always unlocked) and, when the
	// apply-paywall-relaxed variant is active, by the daily-cap-exceeded branch
	// that would otherwise render the hard Lock wall below. `lockState` is the
	// only thing that differs between the two callers.
	// `apply-moment-simplified`: ONE muted line under the CTA replaces the
	// separate unverified-link block, the low-fit block and the Pro upsell, and
	// carries the honest click count. Exactly one primary CTA stays.
	const momentLowFit = lowFitNotice(locale, fitScoreBreakdown);
	const momentProof = applyClicksProof(applyClicks, locale);
	const momentParts = [
		blockingRisk ? applyRiskMessage(blockingRisk, locale) : null,
		momentLowFit ? `${momentLowFit.headline} — ${momentLowFit.detail}` : null,
		momentProof,
	].filter((part): part is string => Boolean(part));
	const applyMomentNotes =
		momentParts.length > 0 ? (
			<p
				data-testid="apply-moment-notes"
				className="text-center text-xs text-muted-foreground"
			>
				{momentParts.join(" · ")}
			</p>
		) : null;

	const renderApplyWithSoftUpsell = (
		lockState: JobApplyOutboundProps["lock_state"],
	) => {
		const isExternal = !isInternalApply;
		const onClick = () => {
			trackApplyClick(jobId, buildApplyPayload(lockState), {
				surface,
				summary_generation_model: summaryGenerationModel ?? null,
			});
			if (isExternal) markPostApplyPending(jobId, { company, title: jobTitle });
			if (isExternal) {
				startTransition(() => loadPostApplyNudge(setPostApplyField));
			}
		};
		return (
			<>
				{banner}
				{showEmailHandoff ? (
					<ApplyEmailHandoff
						jobId={jobId}
						locale={locale}
						variant="primary"
						autoPrepareOnReturn={autoPrepareOnReturn}
						jobTitle={jobTitle}
						company={company}
						applyEmail={applyEmail}
					/>
				) : (
					<Link
						href={outboundApplyUrl}
						target={isInternalApply ? undefined : "_blank"}
						rel={isInternalApply ? undefined : "nofollow"}
						onClick={onClick}
						data-testid="job-apply-cta"
						data-auth-state={authState}
						className={buttonClass({
							variant: applyButtonVariant,
							size: "lg",
							shape: "xl",
							fullWidth: true,
							className: "group",
						})}
					>
						<span data-testid={applyLabelTestId} className="min-w-0 truncate">
							{applyLabel}
						</span>
						<ApplyIcon className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
					</Link>
				)}
				{destinationNote}
				{showEmailHandoff ? (
					<p className="text-center text-xs text-muted-foreground">
						<Link
							href={outboundApplyUrl}
							target="_blank"
							rel="nofollow"
							onClick={onClick}
							className="rounded font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{locale === "id"
								? "Atau tulis sendiri di Gmail"
								: "Or write it yourself in Gmail"}
						</Link>
					</p>
				) : null}
				{guessedEmailNote && !showEmailHandoff ? (
					<p className="text-center text-xs text-muted-foreground">
						{guessedEmailNote}
					</p>
				) : null}
				{simplified ? (
					applyMomentNotes
				) : (
					<p className="text-center text-xs text-muted-foreground">
						<Link
							href={`/${locale}/pricing`}
							onClick={() => {
								trackGateCtaClick({
									surface_id: "apply_pro_hint",
									gate_type: "upgrade",
									trigger: "view_apply",
									job_id: jobId,
									cta_label: "authed_soft_upsell",
								});
								posthog.capture("pricing_cta_clicked", {
									trigger_surface: "job_detail_apply_cta",
									trigger_intent: "soft_upsell",
									job_id: jobId,
									user_state: userState,
									market,
								});
							}}
							className="font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
						>
							{t.upgradeForFullAccess || "Upgrade to Pro"}
						</Link>{" "}
						{t.forUnlimitedJobsShort || "for unlimited jobs"} ·{" "}
						{monthlyPriceDisplayCompact(locale)}/{perMonthWordShort(locale)}
					</p>
				)}
				{isExternal && atsName ? (
					<ExtensionInstallNudge
						locale={locale}
						jobId={jobId}
						atsName={atsName}
					/>
				) : null}
				{postApplyField ? (
					<PostActionSheet
						field={postApplyField}
						surface="post_apply"
						eyebrow={
							t.applicationSentEyebrow ??
							"Application sent · boost your profile"
						}
						autoDismissMs={10_000}
						t={t}
						onClose={() => setPostApplyField(null)}
					/>
				) : null}
			</>
		);
	};

	// `useJobContext()` seeds `userState: "guest"` for EVERY visitor until
	// `/api/me/job-context` resolves — identical for a real guest and an
	// already-authenticated user whose session just hasn't come back yet.
	// Below this point every remaining branch is gated on `userState`, so
	// rendering while unresolved would show an authenticated user the guest
	// signup/upgrade wall for the ~1 fetch round trip until it resolves (T3).
	// This is safe hydration-wise: the seeded value that reaches BOTH the
	// server render and the first client render already has `loading: true`
	// (job-detail-client.tsx:55-58), so server and client agree on rendering
	// this same skeleton — no mismatch. `userState` can never be
	// "free"/"open_profile" while `loading` is true (job-detail-client.tsx
	// only ever sets those together with `loading: false`), so this check is
	// equivalent whether placed before or after the block below; placed here
	// so nothing past this line ever runs against an unresolved auth state.
	// Unresolved auth renders a skeleton rather than a guess: showing the wrong
	// CTA to a visitor entitled to apply is worse than a brief placeholder.
	if (loading) {
		return wrap(
			<div
				className="flex w-full flex-col gap-2"
				data-testid="job-apply-cta-skeleton"
			>
				<Skeleton className="h-12 w-full rounded-xl" />
				<Skeleton className="mx-auto h-3 w-2/3" />
			</div>,
		);
	}

	if (userState === "free" || userState === "open_profile") {
		// canApply = true means the user is authenticated — apply is always
		// unlocked. Show the real button with a soft Pro upsell below.
		if (canApply) {
			return wrap(renderApplyWithSoftUpsell("preview"));
		}

		// apply-paywall-relaxed variant: this user hit the daily-apply cap
		// (canApply === false) but the flag lets them through anyway — render
		// the same unlocked apply button instead of the hard Lock wall below,
		// and record the click as genuinely "unlocked" (not "preview"), since
		// the flag really does let them apply. Control/off keeps the hard wall
		// exactly as-is.
		if (paywallRelaxed) {
			return wrap(renderApplyWithSoftUpsell("unlocked"));
		}

		const onUpgradeClick = () => {
			// The hard upgrade wall (free/open_profile user, apply locked). Fire
			// gate_cta_clicked so the upgrade-gate funnel pairs with the
			// impression, and thread the actual wall cause — a rich-category lock
			// vs a daily-cap lock — instead of the old hardcoded "rich_category".
			trackGateCtaClick({
				surface_id: "apply_upgrade_gate",
				gate_type: "upgrade",
				trigger: "view_apply",
				job_id: jobId,
				cta_label: "hard_wall",
			});
			posthog.capture("pricing_cta_clicked", {
				trigger_surface: "job_detail_apply_cta",
				trigger_intent: categoryType === "rich" ? "rich_category" : "daily_cap",
				job_id: jobId,
				user_state: userState,
				market,
			});
		};
		return wrap(
			<div className="flex w-full flex-col gap-2">
				<Link
					href={`/${locale}/pricing`}
					onClick={onUpgradeClick}
					className={buttonClass({
						size: "lg",
						shape: "xl",
						fullWidth: true,
						className: "group",
					})}
				>
					<Lock className="h-4 w-4" />
					{t.upgradeToViewApply || "Upgrade to view apply link"}
				</Link>
				<p className="text-center text-xs text-muted-foreground">
					{t.applyLockedFreeTier ||
						"Pro unlocks direct apply links for this category."}{" "}
					· {monthlyPriceDisplayCompact(locale)}/{perMonthWordShort(locale)}
				</p>
				{similarJobCount > 0 ? (
					<p className="text-center text-xs text-muted-foreground">
						<a
							href="#related-jobs"
							className="font-medium text-primary underline-offset-4 hover:underline"
						>
							{locale === "id"
								? `Lihat ${similarJobCount} lowongan serupa →`
								: `View ${similarJobCount} similar jobs →`}
						</a>
					</p>
				) : null}
			</div>,
		);
	}

	const encoded = encodeURIComponent(jobPathname);
	// Send new signups straight back to the job they clicked Apply on. The
	// previous /seeker?next= detour discarded their apply intent — 75% of
	// `signup_method: "redirect"` signups never completed (vs 40% for plain
	// magic-link), and the /seeker banner surfacing the return link was buried
	// among dashboard cards. Returning-login users already landed on the job;
	// new signups now get the same. Hunt still owns profile capture during the
	// session, so no profile signal is lost by skipping the dashboard detour.
	const signupHref = `/${locale}/signup?callbackURL=${encoded}`;

	// Guest, rich-category preview tier. The job content is still nominally
	// previewed, but the guest gets a real, unlocked apply CTA, so the click is
	// recorded as "unlocked" (not "preview") — same reasoning as the
	// `paywallRelaxed` branch above: nothing is redacted server-side, the apply
	// link genuinely works, and mislabeling it "preview" would hide real
	// unlocked-click volume from the primary metric.
	// `pickApplyNudge()` is skipped here — it is a server action that assumes an
	// authenticated session, which a guest does not have.
	//
	// `markPostApplyPending()` is NOT skipped, and used to be. It assumes no such
	// thing: it writes `sessionStorage` and dispatches the `ld:apply-clicked`
	// CustomEvent, both of which work fine for a guest. Bundling the two under one
	// "assumes an authenticated session" comment silently darkened every
	// apply-moment surface for this arm — `AfterApplyInline`, `PostApplyOffer` and
	// the paid `ApplyMomentCvOffer` all reveal off that event and none of them
	// could ever fire for a guest. That arm is 312 of 343 apply clicks over the
	// trailing 30 days (91%), so in practice the apply moment was dark for almost
	// everyone who reached it.
	// The guest signup wall was RETIRED on 2026-09-07. `apply-guest-no-signup`
	// (flag 757280) shipped its variant at 100% and has sat there since
	// 2026-08-03; over the trailing 30 days PostHog resolved `variant` for 1,013
	// people and `control` for 1. Yet `apply_signup_gate` still reached 812
	// people in that window, because `useExperiment` starts on the control
	// default and a visitor whose flags never arrive — consent not yet granted,
	// an ad-blocker, a slow `/decide` — fell through the 1.5 s timeout onto the
	// wall. The wall was therefore served by a TELEMETRY FAILURE, not by a
	// product decision, to roughly 800 guests a month who then converted at 4.3%
	// to signup and 11.7% to any apply click at all.
	//
	// Inverting the fallback would have meant "flag absent = feature on", which
	// this repo forbids. Retiring the concluded experiment removes the flag read
	// entirely instead, so there is no resolution left to fail. The post-apply
	// signup nudge below is kept — it is the arm that shipped.
	const isExternal = !isInternalApply;
	const onClick = () => {
		trackApplyClick(jobId, buildApplyPayload("unlocked"), {
			surface,
			summary_generation_model: summaryGenerationModel ?? null,
		});
		if (isExternal) markPostApplyPending(jobId, { company, title: jobTitle });
	};
	return wrap(
		<div className="flex w-full flex-col gap-2">
			{banner}
			{showEmailHandoff ? (
				<>
					<ApplyEmailHandoff
						jobId={jobId}
						locale={locale}
						variant="primary"
						autoPrepareOnReturn={autoPrepareOnReturn}
						jobTitle={jobTitle}
						company={company}
						applyEmail={applyEmail}
					/>
					<p className="text-center text-xs text-muted-foreground">
						<Link
							href={outboundApplyUrl}
							target="_blank"
							rel="nofollow"
							onClick={onClick}
							className="rounded font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{locale === "id"
								? "Atau tulis sendiri di Gmail"
								: "Or write it yourself in Gmail"}
						</Link>
					</p>
				</>
			) : (
				<Link
					href={outboundApplyUrl}
					target={isInternalApply ? undefined : "_blank"}
					rel={isInternalApply ? undefined : "nofollow"}
					onClick={onClick}
					data-testid="job-apply-cta"
					data-auth-state={authState}
					className={buttonClass({
						variant: applyButtonVariant,
						size: "lg",
						shape: "xl",
						fullWidth: true,
						className: "group",
					})}
				>
					<span data-testid={applyLabelTestId} className="min-w-0 truncate">
						{applyLabel}
					</span>
					<ApplyIcon className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
				</Link>
			)}
			{destinationNote}
			{/* Reworded off "track this application" (0/12 engagement in
				    session-replay review, docs/plans/session-replay-ux-audit-2026-08-02.md)
				    — outcome framing (get notified of a reply) instead of a
				    tracking-tool pitch nobody asked for at this moment. */}
			<p className="text-center text-xs text-muted-foreground">
				<Link
					href={signupHref}
					className="rounded font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				>
					{locale === "id"
						? "Daftar gratis, biar kami kabari kalau ada balasan"
						: "Sign up free — get notified if they reply"}
				</Link>
			</p>
		</div>,
	);
}

/** Localized noun for each required profile field, so the banner names the
 *  exact gap ("Add your phone number") instead of a generic "profile incomplete". */
const PROFILE_FIELD_LABEL: Record<string, { en: string; id: string }> = {
	name: { en: "your name", id: "nama" },
	email: { en: "your email", id: "email" },
	phone: { en: "your phone number", id: "nomor telepon" },
	resumeText: { en: "your CV", id: "CV" },
};

function joinFieldLabels(keys: string[], isEn: boolean): string {
	const labels = keys
		.map((k) => PROFILE_FIELD_LABEL[k]?.[isEn ? "en" : "id"])
		.filter((v): v is string => Boolean(v));
	if (labels.length === 0) return isEn ? "your profile" : "profilmu";
	if (labels.length === 1) return labels[0];
	const last = labels[labels.length - 1];
	const head = labels.slice(0, -1).join(", ");
	return isEn ? `${head} and ${last}` : `${head} dan ${last}`;
}

function ProfileIncompleteBanner({
	locale,
	missingKeys,
	t,
}: {
	locale: string;
	missingKeys: string[];
	t: Dictionary;
}) {
	const isEn = locale === "en";
	const [phoneSaved, setPhoneSaved] = useState(false);
	const [phone, setPhone] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [pending, startTransition] = useTransition();

	// Optimistically drop "phone" from the shown gaps once it's saved inline —
	// no refetch needed; if it was the only gap the banner disappears.
	const shownMissing = phoneSaved
		? missingKeys.filter((k) => k !== "phone")
		: missingKeys;
	if (shownMissing.length === 0) return null;

	const needsPhone = shownMissing.includes("phone");
	const needsResume = shownMissing.includes("resumeText");
	const primaryHref = needsResume ? `/${locale}/resume` : `/${locale}/settings`;
	const primaryLabel = needsResume
		? (t.profileIncompleteUploadResume ?? "Upload resume")
		: (t.profileIncompleteCompleteProfile ?? "Complete your profile");
	const title = isEn
		? `Add ${joinFieldLabels(shownMissing, true)} so autofill doesn't miss fields.`
		: `Tambahkan ${joinFieldLabels(shownMissing, false)} agar autofill tidak melewatkan kolom.`;

	function savePhone() {
		setError(null);
		const value = phone.trim();
		if (!value) {
			setError(isEn ? "Enter a phone number." : "Masukkan nomor telepon.");
			return;
		}
		startTransition(async () => {
			let res: Awaited<ReturnType<typeof setProfilePhone>>;
			try {
				res = await setProfilePhone(value);
			} catch (err) {
				// Stale Server Action id after a deploy: reload once onto the new
				// build instead of crashing the job page (same recovery as
				// job-detail-client's mark-applied button).
				if (isServerActionSkewError(err) && reloadOncePerBuild("action")) {
					return;
				}
				setError(
					isEn
						? "Could not save. Refresh the page and try again."
						: "Gagal menyimpan. Muat ulang halaman lalu coba lagi.",
				);
				return;
			}
			if (res && "error" in res && res.error) {
				setError(
					isEn
						? "Enter a valid phone number."
						: "Masukkan nomor telepon yang valid.",
				);
				return;
			}
			setPhoneSaved(true);
		});
	}

	return (
		<div
			data-testid="job-apply-incomplete-banner"
			className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200"
		>
			<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
			<div className="flex flex-1 flex-col gap-1">
				<span className="font-medium">{title}</span>
				{needsPhone ? (
					<form
						className="flex flex-wrap items-center gap-1.5"
						onSubmit={(e) => {
							e.preventDefault();
							savePhone();
						}}
					>
						<label htmlFor="apply-inline-phone" className="sr-only">
							{isEn ? "Phone number" : "Nomor telepon"}
						</label>
						<input
							id="apply-inline-phone"
							type="tel"
							inputMode="tel"
							autoComplete="tel"
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
							placeholder={isEn ? "+62 812 3456 7890" : "+62 812 3456 7890"}
							className="h-7 min-w-0 flex-1 rounded-md border border-amber-300 bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background dark:border-amber-800"
						/>
						<button
							type="submit"
							disabled={pending}
							className="h-7 shrink-0 rounded-md bg-amber-600 px-2.5 font-semibold text-amber-50 transition-colors hover:bg-amber-700 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
						>
							{pending
								? isEn
									? "Saving…"
									: "Menyimpan…"
								: isEn
									? "Save"
									: "Simpan"}
						</button>
					</form>
				) : null}
				{error ? (
					<span className="text-amber-800 dark:text-amber-300">{error}</span>
				) : null}
				<span>
					<Link
						href={primaryHref}
						className="font-semibold underline-offset-4 hover:underline"
					>
						{primaryLabel} →
					</Link>{" "}
					·{" "}
					<ZoneLink
						href={`/${locale}/seeker`}
						className="font-semibold underline-offset-4 hover:underline"
					>
						{t.profileIncompleteChatHunt ?? "Chat with hunt assistant"}
					</ZoneLink>
				</span>
			</div>
		</div>
	);
}
